import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';

import { useAppState } from "../context/AppStateContext";
import { useSSO } from "../context/AuthContext";
import api from "../utils/api";

import { Box, Container } from "@mui/material";

import ResponsiveAppBar from "../components/layout/ResponsiveAppBar";
import Loader from "../components/layout/Loader";
import ErrorModal from "../components/layout/ErrorModal";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import TopCards from "../components/dashboard/TopCards";
import DashboardTabs from "../components/dashboard/DashboardTabs";

const DashboardPage = () => {
  const navigate = useNavigate();

  const { apiRes, setApiRes } = useAppState();
  const { userData } = useSSO();

  if (!userData?.isSuperUser) {
    navigate('page-not-found');
  }

  const dashboardObj = {
    total_proposals: 0,
    total_tokens: 0,
    avg_latency: 0,
    total_cost: 0,
  };
  const dashboardSectionObj = {
    proposalData: {
      avg_score_list: [],
      total_proposals: 0,
      randomness_data: [],
    },
    latencyData: {
      avg_latency_list: [],
    },
    costData: {
      module_usage_list: [],
      user_usage_list: [],
    },
  };
  const [dashboardData, setDashboardData] = useState(dashboardObj);
  const [dashboardSectionData, setDashboardSectionData] = useState(dashboardSectionObj);
  const [activeTab, setActiveTab] = useState('Proposals');
  const [timeRange, setTimeRange] = useState('All Time');

  useEffect(() => {
    getDashboard();
  }, []);

  const getDashboard = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get('/api/dashboard/get', {
        params: { time_range: timeRange }
      });
      setApiRes({ ...apiRes, loading: false });
      setDashboardData(response.data);
    } catch (err) {
      let errorMessage = 'Something went wrong.';
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'No internet connection. Please try again later.';
      } else {
        errorMessage = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      }
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: errorMessage,
        errHeading: 'Dashboard',
      });
    }
  };

  useEffect(() => {
    getDashboardSection();
  }, [activeTab]);

  useEffect(() => {
    getDashboard();
    getDashboardSection();
  }, [timeRange]);

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  const getDashboardSection = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get('/api/dashboard/section/get', {
        params: { section: activeTab, time_range: timeRange }
      });
      setApiRes({ ...apiRes, loading: false });
      setDashboardSectionData(prev => ({
        ...prev,
        proposalData: response.data?.proposal_data,
        latencyData: response.data?.latency_data,
        costData: response.data?.cost_data,
      }));
    } catch (err) {
      let errorMessage = 'Something went wrong.';
      if (!navigator.onLine || err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'No internet connection. Please try again later.';
      } else {
        errorMessage = JSON.stringify(err.response.data).replace(/[{}[\]"']/g, '');
      }
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: errorMessage,
        errHeading: 'Dashboard',
      });
    }
  };

  return (
    <Box className="mainContainer">
      {apiRes.loading && <Loader />}
      <ResponsiveAppBar />
      <Container maxWidth="xl">
        <DashboardHeader
          timeRange={timeRange}
          handleTimeRangeChange={handleTimeRangeChange}
        />
        <TopCards data={dashboardData} />
        <DashboardTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          data={dashboardSectionData}
        />
      </Container>
      <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />
    </Box>
  );
};

export default DashboardPage;